<template>
  <a-form-model v-if="!loading"
                ref="appointmentForm"
                :model="form"
                :label-col="{ span: 6 }"
                :wrapper-col="{ span: 16 }"
                :colon="false"
                :hide-required-mark="true">

    <a-form-model-item ref="summary" prop="summary" label="Onderwerp">
      <a-input v-model="form.summary" placeholder="Geef een titel op" />
      <div v-for="message in errors.summary" class="validation-error">{{ message }}</div>
    </a-form-model-item>

    <a-form-model-item label="Beschrijving">
      <a-textarea v-model="form.description"
                  @keydown="preventEnter"
                  @keyup="replaceNewLineBySpace"
                  placeholder="Indien gewenst kunt u hier een uitgebreidere toelichting van de activiteit geven"
                  :rows="10" />
    </a-form-model-item>

    <a-form-model-item label="Klant">
      <customer-search-by-name-input-component @change="onChangeCustomer" :value="form.customer_id" />
      <div v-for="message in errors.customer_id" class="validation-error">{{ message }}</div>
    </a-form-model-item>

    <a-form-model-item label="Type afspraak">
      <Skeleton class="skeleton" :loading="true" v-if="loadingAppointmentTypes"></Skeleton>
      <a-select ref="appointment_type_id"
                v-else
                placeholder="Maak uw keuze"
                v-model="form.appointment_type_id"
                :disabled="disableAppointmentTypes"
                :show-search="true"
                option-filter-prop="children"
                :options="appointmentTypeOptions" />
      <div v-for="message in errors.appointment_type_id" class="validation-error">{{ message }}</div>
    </a-form-model-item>

    <a-form-model-item label="Resource">
      <Skeleton class="skeleton" :loading="true" v-if="loadingResources"></Skeleton>
      <a-select ref="resource_id" v-else placeholder="Maak uw keuze" v-model="form.resource_id"
                :disabled="disableResources">
        <a-select-option v-for="resource in availableResources" :key="resource.id" :value="resource.id">
          {{ resource.name }}
        </a-select-option>
      </a-select>
      <div v-for="message in errors.resource_id" class="validation-error">{{ message }}</div>
    </a-form-model-item>

    <bookable-times-component :start-time="form.time_start"
                              :end-time="form.time_end"
                              :appointment-type-id="form.appointment_type_id"
                              :resource-id="form.resource_id"
                              :errors="errors"
                              @loading="onLoadingBookableTimes"
                              @changeStartTime="onChangeBookableTimesStart"
                              @changeEndTime="onChangeBookableTimesEnd">
    </bookable-times-component>

    <a-form-model-item label=" " style="margin-bottom: 0.5em">
      <a-switch v-model="form.send_confirmation" :disabled="loading" style="margin-right: 0.5em"/>
      <strong> Stuur een bevestiging naar de klant?</strong>
    </a-form-model-item>

    <a-form-model-item :wrapper-col="{ span: 16, offset: 6 }">
      <a-button id="btn-cancel" @click="onCancel()" type="secondary">
        Annuleren
      </a-button>
      <a-button id="btn-submit" @click="onSave" type="primary" :loading="loadingButton" :disabled="isLoadingSomething">
        Opslaan
      </a-button>
    </a-form-model-item>
  </a-form-model>

  <span v-else>Online Afspraken formulier laden ...</span>
</template>

<script>
import resourceApi from "~/api/online_afspraken_resource";
import appointmentApi from "~/api/online_afspraken_appointment";
import appointmentTypeApi from "~/api/online_afspraken_appointment_type";
import BookableTimesComponent from "./OnlineAfsprakenBookableTimesComponent";
import DateFormatEnum from "~/enums/DateFormatEnum";
import { Skeleton } from "vue-loading-skeleton";
import CustomerSearchByNameInputComponent from "../../customer/components/SearchByNameInputComponent.vue";

export default {
  name: "CalendarEventCreateOrEditAppointmentComponent",
  components: {
    BookableTimesComponent,
    Skeleton,
    CustomerSearchByNameInputComponent
  },
  data() {
    return {
      DateFormatEnum: DateFormatEnum,
      loading: true,
      loadingResources: true,
      loadingAppointmentTypes: true,
      loadingBookableTimes: false,
      loadingButton: false,
      form: {},
      resources: {},
      availableResources: {},
      appointmentTypes: {},
      errors: {}
    };
  },
  props: {
    appointment: {
      type: Object,
      default: () => {
      }
    },
    customerId: {
      type: [String, Number],
      required: false
    }
  },
  watch: {
    "form.appointment_type_id": function(newVal, oldVal) {
      let selectedAppointmentType = null;
      if (Array.isArray(this.appointmentTypes) && newVal) {
        selectedAppointmentType = this.appointmentTypes.find((appointmentType) =>
          appointmentType.id === newVal
        );
      }
      this.setAvailableResources(selectedAppointmentType);
      this.errors.appointment_type_id = {};
    },
    "form.customer_id": function (newVal, oldVal) {
      if (newVal) {
        this.errors.customer_id = {};
      }
    },
    "appointmentTypes": function(newVal, oldVal) {
      let selectedAppointmentType = null;
      if (this.appointment?.appointment_type_id) {
        selectedAppointmentType = newVal.find((appointmentType) =>
          appointmentType.id === this.appointment.appointment_type_id
        );
      }
      this.setAvailableResources(selectedAppointmentType);
    },
    "form.summary": function() {
      this.errors.summary = {};
    },
    "form.time_start": function() {
      this.errors.time_start = {};
    },
    "form.time_end": function() {
      this.errors.time_end = {};
    }
  },
  mounted() {
    this.resetForm();
    this.initializeResources()
        .then(() => {
          this.initializeAppointmentTypes()
              .then(() => {
                this.loading = false;
              });
        });
  },
  computed: {
    appointmentTypeOptions() {
      return this.appointmentTypes.map((appointment) => {
        return {
          value: appointment.id,
          label: appointment.name
        };
      });
    },
    disableAppointmentTypes() {
      return this.loadingBookableTimes;
    },
    disableResources() {
      return this.loadingBookableTimes
        || (typeof this.form.appointment_type_id === "undefined")
        || this.form.appointment_type_id === null;
    },
    isLoadingSomething() {
      return this.loading
        || this.loadingResources
        || this.loadingAppointmentTypes
        || this.loadingBookableTimes;
    }
  },
  methods: {
    initializeResources() {
      return resourceApi.getAll()
        .then((response) => this.resources = response.data)
        .catch((error) => console.error({error, message: "Error fetching resources"}))
        .finally(() => this.loadingResources = false);
    },
    initializeAppointmentTypes() {
      return appointmentTypeApi.getAll()
        .then((response) => this.appointmentTypes = response.data)
        .catch((error) => console.error({error, message: "Error fetching appointment types"}))
        .finally(() => this.loadingAppointmentTypes = false);
    },
    setAvailableResources(selectedAppointmentType) {
      const resourceIds = selectedAppointmentType ? selectedAppointmentType.resourceId.split(",") : [];
      if (Array.isArray(this.resources)) {
        this.availableResources = this.resources.filter((resource) => resourceIds.includes(resource.id.toString()));
      }
    },
    preventEnter(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    },
    replaceNewLineBySpace(event) {
      this.form.description = this.form.description.replace(/\n/g, ' ');
    },
    onChangeCustomer(value) {
      const customer = { "customer_id": value };
      Object.assign(this.form, customer);
    },
    onChangeBookableTimesStart(time) {
      this.form.time_start = time;
    },
    onChangeBookableTimesEnd(time) {
      this.form.time_end = time;
    },
    onSave() {
      this.$refs.appointmentForm.validate(valid => {
        if (valid) {
          this.loadingButton = true;

          if (!this.form.is_visit) {
            this.form.is_visit = null;
          }

          if (this.form.id) {
            appointmentApi.update(this.form)
              .then(() => {
                this.$emit("updated", this.form);
                this.$notification["success"]({
                  message: "Opgeslagen",
                  description: "De afspraak is gewijzigd"
                });
              })
              .catch((errors) => {
                // OnlineAfspraken inconsistency in error response...
                if (errors.response?.data?.error?.errors) {
                  this.errors = errors.response.data.error.errors;
                } else if (errors.response?.data?.errors) {
                  this.errors = Object.assign(this.errors, errors.response.data.errors);
                }

                this.$notification["error"]({
                  message: "Er ging iets mis",
                  description: "Het is niet gelukt de afspraak aan te passen"
                });
              })
              .finally(() => {
                this.loading = false;
                this.loadingButton = false;
              });
          } else {
            appointmentApi.store(this.form)
              .then(() => {
                this.$emit("updated", this.form);
                this.$notification["success"]({
                  message: "Opgeslagen",
                  description: "De afspraak is aangemaakt"
                });
              })
              .catch((errors) => {
                // OnlineAfspraken inconsistency in error response...
                if (errors.response?.data?.error?.errors) {
                  this.errors = errors.response.data.error.errors;
                } else if (errors.response?.data?.errors) {
                  this.errors = Object.assign(this.errors, errors.response.data.errors);
                }

                this.$notification["error"]({
                  message: "Er ging iets mis",
                  description: "Het is niet gelukt de afspraak op te slaan"
                });
              })
              .finally(() => {
                this.loading = false;
                this.loadingButton = false;
              });
          }
        } else {
          return false;
        }
      });
    },
    resetForm() {
      let customerId = null;
      if (typeof this.appointment !== "undefined" && this.appointment !== null
        && typeof this.appointment.customer !== "undefined" && this.appointment.customer !== null
        && typeof this.appointment.customer.data !== "undefined" && this.appointment.customer.data !== null) {
        customerId = this.appointment.customer.data.id;
      } else {
        customerId = this.customerId;
      }

      this.form = {
        appointment_type_id: this.appointment ? this.appointment.appointment_type_id : null,
        customer_id: customerId,
        description: this.appointment ? this.appointment.description : null,
        event_type: this.appointment ? this.appointment.event_type : null,
        id: this.appointment ? this.appointment.id : undefined,
        is_visit: this.appointment ? (this.appointment.is_visit ? this.form.is_visit : "") : "",
        resource_id: this.appointment ? this.appointment.resource_id : null,
        send_confirmation: !this.appointment?.id,
        summary: this.appointment ? this.appointment.summary : null,
        time_end: this.appointment ? this.appointment.time_end : null,
        time_start: this.appointment ? this.appointment.time_start : null
      };
    },
    onCancel() {
      this.$refs.appointmentForm.resetFields();
      this.resetForm();
      this.$emit("close");
    },
    onLoadingBookableTimes(value) {
      this.loadingBookableTimes = value;
    },
  }
};
</script>

<style scoped>
#btn-cancel {
  margin-right: 10px;
  width: calc(50% - 5px);
}

#btn-submit {
  width: calc(50% - 5px);
}

.skeleton {
  border-color: rgb(217, 217, 217);
  border-radius: 4px;
  height: 32px;
  width: 100%;
}

.validation-error {
  font-style: italic;
  color: red;
  text-align: right;
}
</style>
